import { useState, useRef, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router'
import { useTranslation } from 'react-i18next'
import { ChevronUp } from 'lucide-react'
import { cn } from '@/shared/lib/utils'
import { useLangParam } from '@/shared/hooks/use-lang-param'
import { SUPPORTED_LANGS, type SupportedLang } from '@/shared/hooks/use-localized-navigate'

interface Language {
  code: SupportedLang
  flag: string
  label: string
}

const LANGUAGES: Language[] = [
  { code: 'en', flag: '🇬🇧', label: 'English' },
  { code: 'fr', flag: '🇫🇷', label: 'Français' },
]

/**
 * Floating language switcher for landing page
 * Positioned fixed at bottom-right, opens upward on click
 */
export function LanguageSwitcher() {
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const { i18n } = useTranslation()
  const { lang } = useLangParam()
  const navigate = useNavigate()
  const location = useLocation()

  // Current language info
  const currentLang = LANGUAGES.find((l) => l.code === lang) || LANGUAGES[0]

  // Close on click outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  // Close on Escape
  useEffect(() => {
    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
      return () => document.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen])

  const switchLanguage = (newLang: SupportedLang) => {
    if (newLang === lang) {
      setIsOpen(false)
      return
    }

    // Update i18next
    i18n.changeLanguage(newLang)

    // Update URL - replace language prefix or add it
    const currentPath = location.pathname
    const langPrefixRegex = new RegExp(`^/(${SUPPORTED_LANGS.join('|')})(/|$)`)

    let newPath: string
    if (langPrefixRegex.test(currentPath)) {
      // Replace existing prefix
      newPath = currentPath.replace(langPrefixRegex, `/${newLang}$2`)
    } else {
      // Add prefix (for paths like '/')
      newPath = currentPath === '/' ? `/${newLang}` : `/${newLang}${currentPath}`
    }

    // Preserve query string and hash
    const search = location.search || ''
    const hash = location.hash || ''

    navigate(newPath + search + hash)
    setIsOpen(false)
  }

  return (
    <div
      ref={containerRef}
      className="fixed bottom-6 right-6 z-dropdown"
    >
      {/* Dropdown list (opens upward) */}
      <div
        className={cn(
          'absolute bottom-full right-0 mb-2 overflow-hidden rounded-lg bg-white shadow-lg ring-1 ring-black/5 transition-all duration-200',
          isOpen
            ? 'visible translate-y-0 opacity-100'
            : 'invisible translate-y-2 opacity-0'
        )}
      >
        {LANGUAGES.filter((l) => l.code !== lang).map((language) => (
          <button
            key={language.code}
            onClick={() => switchLanguage(language.code)}
            className="flex w-full items-center gap-2.5 px-4 py-2.5 text-sm text-foreground transition-colors hover:bg-background-secondary"
          >
            <span className="text-base">{language.flag}</span>
            <span>{language.label}</span>
          </button>
        ))}
      </div>

      {/* Toggle button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-label="Change language"
        className={cn(
          'flex items-center gap-2 rounded-lg bg-white px-4 py-2.5 shadow-lg ring-1 ring-black/5 transition-all duration-200',
          'hover:shadow-xl',
          isOpen && 'ring-2 ring-accent'
        )}
      >
        <span className="text-base">{currentLang.flag}</span>
        <span className="text-sm font-medium text-foreground">{currentLang.code.toUpperCase()}</span>
        <ChevronUp
          className={cn(
            'h-4 w-4 text-foreground-muted transition-transform duration-200',
            isOpen ? 'rotate-180' : 'rotate-0'
          )}
        />
      </button>
    </div>
  )
}
